/**
 * Reset Billing Action - WHAT Section
 *
 * Edit mode UI for resetting subscription billing cycle anchor.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-reset-billing-cycle.hbs
 *
 * Fields:
 * - prorationBehavior: How to handle charges (create_prorations, none, always_invoice)
 * - reminderOnId: Optional specific subscription item ID
 * - reminderOnType: Subscription item type
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';

import { ActionWhatSectionProps } from '../automationActions';
import { fetchSubscriptionItems } from '../utils/sellableItems';

/**
 * ResetBilling WHAT Section - Edit mode
 */
export const ResetBillingWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue, values } = useFormikContext<{
    automations: Array<{
      _subscriptionValue?: string;
      reminderOnType?: string;
      reminderOnId?: string | number;
      prorationBehavior?: string;
    }>;
  }>();

  // V2 uses: no_charge, no_prorate, prorate
  const prorationOptions = [
    { value: 'no_charge', label: t('automations.resetBilling.doNotCharge') },
    { value: 'no_prorate', label: t('automations.resetBilling.chargeFullAmount') },
    { value: 'prorate', label: t('automations.resetBilling.chargeProrated') },
  ];

  // Fetch subscription items
  const { data: items = [] } = useQuery({
    queryKey: ['sellable-items', 'subscription'],
    queryFn: fetchSubscriptionItems,
    staleTime: 60000,
  });

  const itemOptions = [
    { value: '', label: t('automations.resetBilling.mostRecentActiveSubscription') },
    ...items.map((item) => ({
      value: `${item.type}:${item.id}`,
      label: item.name,
    })),
  ];

  // Sync the compound value field with the underlying fields
  useEffect(() => {
    const current = values.automations[index];
    if (current?.reminderOnType && current?.reminderOnId) {
      const expected = `${current.reminderOnType}:${current.reminderOnId}`;
      if (current._subscriptionValue !== expected) {
        setFieldValue(`automations.${index}._subscriptionValue`, expected);
      }
    } else if (current?._subscriptionValue !== '') {
      // Clear the compound value if underlying values are cleared
      if (!current?.reminderOnType && !current?.reminderOnId) {
        setFieldValue(`automations.${index}._subscriptionValue`, '');
      }
    }
  }, [automation.reminderOnType, automation.reminderOnId, index, setFieldValue, values.automations]);

  const handleItemChange = (value: string) => {
    if (!value) {
      setFieldValue(`automations.${index}.reminderOnId`, null);
      setFieldValue(`automations.${index}.reminderOnType`, null);
      return;
    }
    const [type, id] = value.split(':');
    setFieldValue(`automations.${index}.reminderOnId`, id);
    setFieldValue(`automations.${index}.reminderOnType`, type);
  };

  return (
    <Stack spacing={2}>
      {/* Proration Behavior */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.resetBilling.chargeBehavior')}</Box>
        <Box sx={{ minWidth: 280 }}>
          <TextField
            value={values.automations[index]?.prorationBehavior || ''}
            onChange={(e) =>
              setFieldValue(`automations.${index}.prorationBehavior`, e.target.value)
            }
            options={prorationOptions}
            placeholder={t('automations.resetBilling.selectChargeBehavior')}
            size="small"
          />
        </Box>
      </Box>

      {/* Subscription selector */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.resetBilling.resetThisSubscription')}</Box>
        <Box sx={{ minWidth: 280 }}>
          <TextField
            value={values.automations[index]?._subscriptionValue || ''}
            onChange={(e) => handleItemChange(e.target.value as string)}
            options={itemOptions}
            size="small"
          />
        </Box>
      </Box>

      {/* Helper text */}
      <Typography variant="body2" color="text.secondary">
        {t('automations.resetBilling.helperText')}
      </Typography>
    </Stack>
  );
};

/**
 * ResetBilling What Summary Component
 */
export const ResetBillingWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: items = [] } = useQuery({
    queryKey: ['sellable-items', 'subscription'],
    queryFn: fetchSubscriptionItems,
    staleTime: 60000,
  });

  const prorationLabelMap: Record<string, string> = {
    no_charge: t('automations.resetBilling.doNotCharge').toLowerCase(),
    no_prorate: t('automations.resetBilling.chargeFullAmount').toLowerCase(),
    prorate: t('automations.resetBilling.chargeProrated').toLowerCase(),
  };
  const prorationLabel =
    prorationLabelMap[String(automation.prorationBehavior)] || 'unknown behavior';

  if (!automation.prorationBehavior) {
    return <i>{t('automations.resetBilling.pleaseSelectBehavior')}</i>;
  }

  let subscriptionText = t('automations.resetBilling.accountMostRecentSubscription');
  if (automation.reminderOnId && automation.reminderOnType) {
    const item = items.find(
      (i) => i.id != null && i.id.toString() === automation.reminderOnId?.toString()
    );
    subscriptionText = item?.name || `Subscription #${automation.reminderOnId}`;
  }

  return (
    <>
      {t('automations.resetBilling.resetBillingCycleAnchor', {
        behavior: prorationLabel,
        subscription: subscriptionText,
      })}
    </>
  );
};
